/**
 * Client data for the /practice pages: static JSON emitted by the prerender
 * into dist/practice-data/ (served as plain assets — no API involved; the dev
 * server serves the same shapes via the vite middleware in vite.config.ts).
 *
 * The SSR prerender seeds these exact query keys (entry-server SeoSeed), so the
 * build-time render resolves synchronously; the client re-render fetches the
 * static file.
 */
import { useQuery } from "@tanstack/react-query";
import type {
  PracticeGradeHub,
  PracticeIndexData,
  PracticeSkillLink,
  PracticeTopicPage,
  TestRelatedLinks,
} from "../lib/practice-types.js";
import type { AsyncResult } from "./useApi.js";

/** Thrown (as Error message) when the path resolves to the SPA shell instead of
 *  JSON — i.e. the route params don't name a real page. Routes 404 on it. */
export const PRACTICE_NOT_FOUND = "practice_not_found";

async function fetchPracticeJson<T>(path: string): Promise<T> {
  const res = await fetch(path);
  // An unknown path falls through Vercel's rewrites to the SPA shell (HTML,
  // status 200) rather than a 404 — treat anything non-JSON as not-found.
  const isJson = res.headers.get("content-type")?.includes("application/json") ?? false;
  if (!res.ok || !isJson) throw new Error(PRACTICE_NOT_FOUND);
  return (await res.json()) as T;
}

function usePracticeJson<T>(key: readonly unknown[], path: string | null): AsyncResult<T> {
  const query = useQuery<T>({
    queryKey: key,
    queryFn: () =>
      path ? fetchPracticeJson<T>(path) : Promise.reject(new Error(PRACTICE_NOT_FOUND)),
    enabled: path !== null,
    retry: false, // a missing static file won't appear on retry
    staleTime: Infinity, // immutable per deploy
    gcTime: 10 * 60_000,
  });
  return {
    data: query.data ?? null,
    err: query.error ? query.error.message : null,
    loading: query.isPending,
    reload: () => {
      void query.refetch();
    },
  };
}

/** Grade URL segments are lowercase bank grade dirs: grade-k, grade-1 … grade-13. */
export function isValidGradeSlug(gradeSlug: string | undefined): gradeSlug is string {
  return typeof gradeSlug === "string" && /^grade-(k|[1-9]|1[0-3])$/.test(gradeSlug);
}

export function usePracticeIndex(): AsyncResult<PracticeIndexData> {
  return usePracticeJson<PracticeIndexData>(["practice-index"], "/practice-data/index.json");
}

export function usePracticeHub(gradeSlug: string | undefined): AsyncResult<PracticeGradeHub> {
  const ok = isValidGradeSlug(gradeSlug);
  return usePracticeJson<PracticeGradeHub>(
    ["practice-hub", gradeSlug],
    ok ? `/practice-data/${gradeSlug}.json` : null,
  );
}

export function usePracticeTopic(
  gradeSlug: string | undefined,
  topicSlug: string | undefined,
): AsyncResult<PracticeTopicPage> {
  const ok = isValidGradeSlug(gradeSlug) && !!topicSlug && /^[a-z0-9-]+$/.test(topicSlug);
  return usePracticeJson<PracticeTopicPage>(
    ["practice-topic", gradeSlug, topicSlug],
    ok ? `/practice-data/${gradeSlug}/${topicSlug}.json` : null,
  );
}

/** Test-landing pages' "practice these skills" / "games that drill it" block.
 *  A slug with no coverage-map entry 404s (not every JSON path exists) — the
 *  route just renders nothing, same fail-soft behavior as a missing practice page. */
export function useTestRelated(slug: string | undefined): AsyncResult<TestRelatedLinks> {
  const ok = !!slug && /^[a-z0-9-]+$/.test(slug);
  return usePracticeJson<TestRelatedLinks>(
    ["test-related", slug],
    ok ? `/test-related-data/${slug}.json` : null,
  );
}

/** Game detail pages' "practice this skill" block. A slug with no matching
 *  topic 404s — the section just renders nothing, same fail-soft behavior as
 *  useTestRelated. */
export function useGameRelated(slug: string | undefined): AsyncResult<PracticeSkillLink[]> {
  const ok = !!slug && /^[a-z0-9-]+$/.test(slug);
  return usePracticeJson<PracticeSkillLink[]>(
    ["game-related", slug],
    ok ? `/game-related-data/${slug}.json` : null,
  );
}
